// docs/116 the inn's own life on the stage: its furniture (`Furniture`), the candles on every table, the
// crowd drawn from the visit's seed (CI1), the bard and the hearthside storyteller (CI7, CI9), the serving
// hands and the kitchen bell (CI14), the regulars at the dice table, the cat and the dog (CI13, `Animals`),
// what is on the tables in each season (CI15), and the moments the room shares: toasts and drinks slid
// down the counter (CI6, CI12), the dice table's big throws (CI5), clapping along to the bard, the tale's
// applause, and upstairs a night's sleep (CI16). Everything every player should see alike (the tales, the
// bell, the candles) runs off `serverNow()` slots. docs/121 VW1/VW2: so does the rest: the guests'
// chatter is a round of the World's clock and the visit's seed, the guest on the gallery strolls on the
// same clock, and a toast's voices, a drink's thanks and the dice table's shouts are picked from the
// module's seed for the moment (or the game's throw), so every view hears the same words from the same
// people.
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { playSfx } from '../../../../../core/audio';
import { serverNow } from '../../../../../core/time';
import { CELL, hashN, type Station } from '../../layout';
import { Dish, Sprite } from '../../sprites';
import { asideSpots, SEAT_GAP } from '../../seats';
import { pickBy, roundOf, shuffleBy, wanderStep } from '../../scenery';
import { findPath, nearestFree, type Pt } from '../../walk';
import { reactionOf } from '../reactions';
import { currentSong, isFeast } from '../song';
import { TUNES } from '../tunes';
import { taleAt, LINE_MS } from '../tales';
import { useInn } from '../store';
import { POSE, type DiceView, type DrinkKind } from '../types';
import type { InnStage } from '../stage';
import { poseStations } from '../stations';
import { crowdOf, type Guest } from './crowd';
import { useLife, type GuestAct } from './store';
import { Furniture } from './Furniture';
import { Animals } from './Animals';
import { bellArt, flowerJug, fruitBowl, gourd, holly, luteArt, mugArt, stickArt } from './art';
import type { Pix as PixArt } from '../paint';

/** Inn people's looks (Oryx townsfolk frames). */
const BARD_FRAME = 77, TELLER_FRAME = 79, COOK_FRAME = 43, MAID_FRAME = 76;
const KITCHEN_MS = 80_000, CANDLE_MS = 240_000;
const TALK = ['Ha!', '…and then the goblin says…', 'Another round?', 'You never did!', 'Mm, good ale.', 'Rain\'s coming, mark me.', 'Hah, true enough.', '…', 'My cousin saw a wyvern once.', 'Don\'t tell my wife.', 'Pass the salt.', 'Is that so?', 'Fancy that.', 'Thirsty work, ploughing.', 'Who\'s paying?', 'Ha ha!', 'Not the goat again…', 'Shh, listen…'];
const NIGHT_TALK = ['*yawn*', 'One more, then bed.', 'Late, isn\'t it?', 'Hear that wind?', '*hic*'];
const TOASTS = ['To the band!', 'Sláinte!', 'Cheers!', 'To absent friends!', 'Your health!', 'Down the hatch!', 'To the road!', 'Huzzah!'];
const DICE_FACE: Partial<Record<string, GuestAct>> = { cheer: 'cheer', win: 'cheer', gasp: 'gasp', groan: 'groan', laugh: 'laugh', phew: 'nod', kitty: 'laugh' };

export function InnLife({ stage }: { stage: InnStage }): React.JSX.Element {
  // a new room (upstairs and down, a new size) starts the life afresh
  return <Life key={`${stage.upstairs ? 'up' : 'inn'}:${stage.room.w}x${stage.room.h}`} stage={stage} />;
}

function Pix({ art, scale, className = '' }: { art: PixArt; scale: number; className?: string }) {
  return <span className={`fk-village__pix ${className}`} style={{ width: art.w * scale, height: art.h * scale, backgroundImage: `url(${art.url})`, backgroundSize: '100% 100%' }} />;
}

/** A click on one of the inn's people goes to the place under them (the bard's corner, the hearth, the dice table). */
function clickThrough(e: React.MouseEvent) {
  const spot = document.elementsFromPoint(e.clientX, e.clientY).find((el) => el instanceof HTMLElement && el.classList.contains('fk-inn__spot'));
  if (spot instanceof HTMLElement) spot.click();
}

/** The server clock, a tick a second (for what burns down and what is told). */
function useSecond(): number {
  const [now, setNow] = useState(serverNow);
  useEffect(() => { const t = window.setInterval(() => setNow(serverNow()), 1000); return () => window.clearInterval(t); }, []);
  return now;
}

interface Person { key: string; name: string; what: string; frame: number; at: Station; kind: 'bard' | 'teller' | 'regular' | 'guest'; mug?: boolean; seated?: boolean }

function Life({ stage }: { stage: InnStage }) {
  const { room, plan, mover, grid, scale, cellPx, data, look, upstairs } = stage;
  const live = useRef(stage);
  live.current = stage;
  const night = look.night;
  const feast = isFeast(data.seasonWeek);
  const seed = data.visitSeed || look.seed;
  const now = useSecond();
  const acts = useLife((s) => s.acts);
  const dishes = useLife((s) => s.dishes);
  const beat = useLife((s) => s.beat);
  const playing = useInn((s) => s.playing);

  // ---- who is here: the crowd (CI1), the bard, the storyteller, the dice table's regulars ----
  const folkN = stage.folk.length;
  const crowd = useMemo<Guest[]>(() => (upstairs ? [] : crowdOf(seed, night, room, plan, room.others.slice(0, Math.max(0, folkN - 1)))), [upstairs, seed, night, room, plan, folkN]);
  const regulars = upstairs || !data.dice ? [] : data.dice.seats.flatMap((s, i) => (s.kind === 'regular' && plan.dice.stools[i] ? [{ key: `i:reg${i}`, name: s.name, what: ['a cautious regular', 'a steady regular', 'a bold regular'][s.temper ?? 1] ?? 'a regular', frame: s.frame || 75, at: plan.dice.stools[i]!, kind: 'regular' as const, seated: true }] : []));
  const people: Person[] = upstairs ? [] : [
    { key: 'i:bard', name: 'Fennick the bard', what: 'plays for coin and applause', frame: BARD_FRAME, at: plan.bard, kind: 'bard', seated: true },
    { key: 'i:teller', name: 'Old Grandmother Hask', what: 'tells tales by the fire', frame: TELLER_FRAME, at: plan.storyteller, kind: 'teller', seated: true },
    // someone staying the night, up on the gallery
    ...(plan.gallery ? [{ key: 'i:upper', name: 'Wenna Applewhite', what: 'staying the night upstairs', frame: [40, 74, 39, 110][seed % 4]!, at: { x: (plan.gallery.doors[0] ?? 8) + 1.3, y: plan.gallery.y - 1, flip: seed % 2 === 0 }, kind: 'guest' as const }] : []),
    ...crowd.map((g) => ({ key: g.key, name: g.name, what: g.what, frame: g.frame, at: g.at, kind: 'guest' as const, mug: g.mug, ...(g.at.seated ? { seated: true } : {}) })),
    ...regulars,
  ];
  const peopleRef = useRef(people);
  peopleRef.current = people;
  // place everyone once (the motion engine keeps them where they are from then on)
  for (const p of people) if (!mover.at(p.key)) mover.place(p.key, p.at, p.at.flip ? -1 : 1, p.seated);
  // a regular who has left their stool is forgotten
  const regKeys = regulars.map((r) => r.key).join();
  useEffect(() => { for (const k of mover.keys()) if (k.startsWith('i:reg') && !regKeys.split(',').includes(k)) mover.drop(k); }, [regKeys]);
  useEffect(() => () => {
    for (const k of mover.keys()) if (k.startsWith('g:') || k.startsWith('i:')) mover.drop(k);
    useLife.getState().clear();
  }, []);

  // ---- helpers the directors share ----
  const walk = (k: string, to: Pt, speed: number, done?: () => void, seatAt?: Station) => {
    const cur = mover.at(k);
    if (!cur) return;
    const from = cur.seated ? { x: cur.x, y: cur.y - 0.25 } : cur;
    if (cur.seated) mover.place(k, from, cur.face);
    const path = findPath(grid, from, to);
    mover.walk(k, seatAt ? [...path, seatAt] : path, { speed, done: () => { if (seatAt) mover.place(k, seatAt, seatAt.flip ? -1 : 1, true); done?.(); } });
  };
  const faceTo = (k: string, x: number) => { const p = mover.at(k); if (p && Math.abs(p.x - x) > 0.2) mover.face(k, x > p.x ? 1 : -1); };
  const keeperKey = () => {
    for (const k of live.current.folk) { const p = mover.at(`n:${k}`); if (p && Math.abs(p.x - room.keeper.x) < 0.5 && Math.abs(p.y - room.keeper.y) < 0.5) return `n:${k}`; }
    return undefined;
  };
  const guestsFree = () => peopleRef.current.filter((p) => p.kind === 'guest' && !useLife.getState().acts[p.key] && !mover.walking(p.key));

  // ---- CI7: the bard's tune: the beat the room claps to, notes off the lute ----
  useEffect(() => {
    const L = useLife.getState();
    if (!playing) { L.setBeat(undefined); L.act('i:bard', undefined); return; }
    const t = TUNES[playing.tune];
    if (!t) return;
    L.act('i:bard', 'play');
    const tick = () => {
      const s = (serverNow() - playing.startedAtMs) / 1000;
      const beatS = 60 / t.bpm;
      if (s >= t.clapFrom && s < t.clapTo && t.claps.length) {
        const period = beatS * (t.beatsPerBar / t.claps.length);
        const first = t.offset + (t.claps[0] ?? 0) * beatS;
        useLife.getState().setBeat({ period, delay: first, stomp: t.stomps.length > 0 });
      } else useLife.getState().setBeat(undefined);
      if (!stage.reduced && hashN(seed, Math.floor(serverNow() / 1000), 0xba7d) < 0.5) { const b = mover.at('i:bard'); if (b) useLife.getState().burst('notes', b.x + 0.3, b.y - 0.1, 1600); }
    };
    tick();
    const iv = window.setInterval(tick, 1000);
    return () => { window.clearInterval(iv); useLife.getState().setBeat(undefined); };
  }, [playing?.tune, playing?.startedAtMs]);

  // ---- CI9: the storyteller's tale, line by line; the room listens, and claps at the end ----
  const told = useRef({ slot: -1, line: -1, clapped: -1 });
  useEffect(() => {
    if (upstairs) return;
    const busy = (ms: number) => !!currentSong(data.song, seed, ms, look.season, feast);
    const tn = taleAt(now, data.deeds, look.seed, busy);
    const L = useLife.getState();
    if (!tn) return;
    const T = told.current;
    if (tn.line >= 0 && (tn.slot !== T.slot || tn.line !== T.line) && now < tn.endMs) {
      T.slot = tn.slot; T.line = tn.line;
      L.say('i:teller', tn.tale.lines[tn.line]!, LINE_MS - 250, 'tale');
      L.act('i:teller', 'tell', LINE_MS);
      const tl = mover.at('i:teller');
      if (tl) for (const g of peopleRef.current) if (g.kind === 'guest' && !mover.walking(g.key)) { faceTo(g.key, tl.x); if (!L.acts[g.key] || L.acts[g.key] === 'listen') L.act(g.key, 'listen', LINE_MS + 400); }
    }
    if (now >= tn.endMs && T.clapped !== tn.slot && now < tn.endMs + 3000) {
      T.clapped = tn.slot;
      playSfx('inn.applause', { gain: 0.55 });
      for (const g of peopleRef.current) if (g.kind === 'guest') L.act(g.key, 'clap', 2600);
      L.act('i:teller', 'nod', 2000);
      const pick = peopleRef.current.filter((p) => p.kind === 'guest');
      const w = pick.length ? pickBy(pick, hashN(seed, tn.slot, 0xc1a9)) : undefined;
      if (w) L.say(w.key, pickBy(['Bravo!', 'Another!', 'Hear, hear!', 'Ha! Lovely.'], hashN(seed, tn.slot, 0xc1aa)), 2000, 'shout');
    }
  }, [now]);

  // ---- CI14: the kitchen bell: a serving hand brings a guest a plate and a mug; later clears it ----
  const kitchen = useRef({ slot: -1, served: -1, cleared: -1 });
  const seatedGuests = crowd.filter((g) => g.how === 'sit' && g.table !== undefined);
  useEffect(() => {
    if (upstairs || !seatedGuests.length) return;
    const slot = Math.floor(now / KITCHEN_MS), t = now - slot * KITCHEN_MS;
    const K = kitchen.current;
    const g = seatedGuests[Math.floor(hashN(seed, slot, 9) * seatedGuests.length)]!;
    const table = room.tables?.[g.table!];
    if (!table) return;
    const L = useLife.getState();
    const dishKey = `k:${g.key}`;
    const kind = hashN(seed, slot, 10) < 0.5 ? 'stew' : 'bread';
    const plate = { key: dishKey, x: g.at.x + 0.02, y: table.y, kind, left: 0 } as const;
    const helperBusy = !!mover.at('helper');
    if (K.slot !== slot) {
      K.slot = slot;
      if (t < 3000 && !helperBusy) {
        // the bell, then the cook out of the kitchen with the plate
        playSfx('inn.bell', { gain: 0.6 });
        L.act('i:bell', 'play', 1600);
        window.setTimeout(() => {
          mover.place('i:cook', plan.kitchen, -1);
          L.act('i:cook', 'serve');
          const by = nearestFree(grid, { x: g.at.x + (g.at.x > table.x + table.w / 2 ? 0.9 : -0.9), y: Math.max(grid.y0, g.at.y - 0.1) });
          walk('i:cook', by, 2.1, () => {
            faceTo('i:cook', g.at.x);
            L.dish(plate); L.dish({ key: `${dishKey}:mug`, x: g.at.x + 0.5, y: table.y, kind: 'mug', left: 0 });
            playSfx('inn.mug', { gain: 0.4 });
            L.act('i:cook', undefined);
            K.served = slot;
            // the guest eats: three bites, the plate empties
            L.act(g.key, 'eat', 9500);
            window.setTimeout(() => L.dish({ ...plate, left: 1 }), 3200);
            window.setTimeout(() => L.dish({ ...plate, left: 2 }), 6400);
            window.setTimeout(() => { L.say(g.key, pickBy(['Mm!', 'Lovely.', 'Best in the realm.', 'Compliments to the cook!'], hashN(seed, slot, 11)), 1800); }, 9600);
            window.setTimeout(() => walk('i:cook', { x: plan.kitchen.x, y: grid.y0 }, 2.1, () => mover.drop('i:cook')), 800);
          });
        }, 1100);
      } else if (t < 50_000) {
        // came in after the plate was served: it is there, eaten
        L.dish({ ...plate, left: 2 });
      }
    }
    if (t >= 52_000 && K.cleared !== slot) {
      K.cleared = slot;
      if (!useLife.getState().dishes.some((d) => d.key === dishKey)) return;
      if (helperBusy || mover.at('i:cook')) { L.undish(dishKey); L.undish(`${dishKey}:mug`); return; }
      mover.place('i:cook', plan.kitchen, -1);
      const by = nearestFree(grid, { x: g.at.x + (g.at.x > table.x + table.w / 2 ? 0.9 : -0.9), y: Math.max(grid.y0, g.at.y - 0.1) });
      walk('i:cook', by, 2.1, () => {
        faceTo('i:cook', g.at.x);
        L.undish(dishKey); L.undish(`${dishKey}:mug`);
        L.act('i:cook', 'serve');
        window.setTimeout(() => walk('i:cook', { x: plan.kitchen.x, y: grid.y0 }, 2.1, () => { L.act('i:cook', undefined); mover.drop('i:cook'); }), 500);
      });
    }
  }, [now]);

  // ---- CI14: the candles burn down; a serving hand puts a fresh one in when one is low ----
  const candleTrip = useRef(new Map<number, number>());
  const burn = (i: number) => { const off = Math.floor(hashN(look.seed, i, 13) * CANDLE_MS); return { frac: ((now + off) % CANDLE_MS) / CANDLE_MS, cycle: Math.floor((now + off) / CANDLE_MS), toWrap: CANDLE_MS - ((now + off) % CANDLE_MS) }; };
  useEffect(() => {
    if (upstairs || mover.at('i:maid')) return;
    for (const [i, cd] of plan.candles.entries()) {
      const b = burn(i);
      if (b.toWrap > 13_000 || b.toWrap < 4_000 || candleTrip.current.get(i) === b.cycle) continue;
      candleTrip.current.set(i, b.cycle);
      const L = useLife.getState();
      mover.place('i:maid', plan.kitchen, -1);
      L.act('i:maid', 'serve');
      const by = nearestFree(grid, { x: cd.x + 0.9, y: Math.max(grid.y0, cd.y - 0.9) });
      const wrapAt = performance.now() + b.toWrap;
      walk('i:maid', by, 2, () => {
        faceTo('i:maid', cd.x);
        window.setTimeout(() => {
          playSfx('inn.candle', { gain: 0.5 });
          L.say('i:maid', ['There we are.', 'Fresh one.', 'Let there be light!'][i % 3]!, 1500);
          window.setTimeout(() => walk('i:maid', { x: plan.kitchen.x, y: grid.y0 }, 2, () => { L.act('i:maid', undefined); mover.drop('i:maid'); }), 900);
        }, Math.max(200, wrapAt - performance.now() + 150));
      });
      break;
    }
  }, [now]);

  // ---- the room's chatter: someone says something, laughs, sips, turns to their neighbour ----
  // docs/121 VW1: once a round of the World's clock; who and what from the visit's seed and the round
  useEffect(() => {
    if (upstairs) return;
    const period = playing ? 4.6 : 3.6;
    let last = roundOf(serverNow() / 1000, period).k;
    const iv = window.setInterval(() => {
      const k = roundOf(serverNow() / 1000, period).k;
      if (k !== last) { last = k; chatter(k); }
    }, 250);
    let on = true;
    const chatter = (k: number) => {
      const h = (i: number) => hashN(seed, k, 0xc4a7, i);
      const L = useLife.getState();
      if (L.bubbles.some((b) => b.kind === 'tale')) return;
      const guests = peopleRef.current.filter((x) => x.kind === 'guest');
      if (!guests.length) return;
      const g = pickBy(guests, h(0));
      if (mover.walking(g.key) || L.acts[g.key] === 'listen' || L.acts[g.key] === 'clap') return;
      const roll = h(1);
      if (roll < 0.38) {
        const lines = night && h(2) < 0.35 ? NIGHT_TALK : TALK;
        L.say(g.key, pickBy(lines, h(3)), 2300);
        // a neighbour answers
        const me = mover.at(g.key);
        const nb = me && guests.find((o) => o.key !== g.key && (() => { const p = mover.at(o.key); return p && Math.hypot(p.x - me.x, p.y - me.y) < 2.2; })());
        if (nb && me) {
          const p = mover.at(nb.key)!;
          faceTo(nb.key, me.x); faceTo(g.key, p.x);
          if (h(4) < 0.6) window.setTimeout(() => { if (on) L.say(nb.key, pickBy(['Ha!', 'Never!', 'Aye.', 'Hah, true.', 'Go on…', 'No!'], h(5)), 1800); }, 1400);
        }
      } else if (roll < 0.62 && g.mug) L.act(g.key, 'sip', 1500);
      else if (roll < 0.78) { L.act(g.key, 'laugh', 1400); if (h(6) < 0.25) playSfx('inn.laugh', { gain: 0.22 }); }
      else { const p = mover.at(g.key); if (p) mover.face(g.key, p.face > 0 ? -1 : 1); }
    };
    return () => { on = false; window.clearInterval(iv); };
  }, [playing ? 1 : 0, upstairs, seed]);

  // ---- CI6/CI12: a toast and a drink are moments everyone in the room sees at once ----
  const seen = useRef<Set<number> | undefined>(undefined);
  const cheersSig = data.cheers.map((c) => c.n).join();
  useEffect(() => {
    if (!seen.current) { seen.current = new Set(data.cheers.map((c) => c.n)); return; }
    for (const c of data.cheers) {
      if (seen.current.has(c.n)) continue;
      seen.current.add(c.n);
      if (c.kind === 'toast') toast(c.seed);
      else if (c.kind === 'drink' && c.heroKey) drink(c.heroKey, c.drink ?? 'ale', c.seed);
      else if (c.kind === 'song') { const L = useLife.getState(); L.say('i:bard', 'Ah, a request!', 2200); for (const g of guestsFree().slice(0, 3)) L.act(g.key, 'cheer', 1600); }
    }
  }, [cheersSig]);
  const toast = (cs: number) => {
    if (upstairs) return;
    const L = useLife.getState();
    const S = live.current;
    const mugs = useInn.getState().mugs;
    const withMug = S.heroes.filter((h) => mugs[h.key]).map((h) => h.key);
    const without = S.mine.filter((h) => !h.companion && !mugs[h.key]).map((h) => h.key);
    if (withMug.length) useInn.getState().flash(withMug, 'toast', 2800);
    if (without.length) useInn.getState().flash(without, 'cheer', 2400);
    for (const p of peopleRef.current) if (p.kind !== 'bard' && p.kind !== 'teller') L.act(p.key, p.mug || p.kind === 'regular' ? 'raise' : 'cheer', 2800);
    const kk = keeperKey();
    if (kk) L.act(kk, 'raise', 2800);
    playSfx('inn.clink', { gain: 0.8, delayMs: 350 });
    playSfx('inn.cheers', { gain: 0.75, delayMs: 520 });
    // docs/121 VW2: the same four voices and words in every view (the toast's seed over everyone in the room, in key order)
    const voices = shuffleBy([...peopleRef.current.map((p) => p.key), ...S.heroes.map((h) => `h:${h.key}`), ...(kk ? [kk] : [])].sort(), cs, 0x7035).slice(0, 4);
    voices.forEach((k, i) => window.setTimeout(() => L.say(k, pickBy(TOASTS, hashN(cs, i, 0x7036)), 2000, 'shout'), 300 + i * 260));
    for (const k of [...withMug.map((x) => `h:${x}`), ...peopleRef.current.filter((p) => p.mug).map((p) => p.key)].slice(0, 6)) {
      const p = mover.at(k);
      if (p) window.setTimeout(() => L.burst('clink', p.x + 0.5, p.y - 0.3, 900), 350);
    }
  };
  const drink = (heroKey: string, kind: DrinkKind, cs: number) => {
    const L = useLife.getState();
    const kk = keeperKey();
    const tap = plan.bar.tap;
    if (kk) { faceTo(kk, tap.x + 1); L.act(kk, 'pour', 1600); }
    playSfx('inn.pour', { gain: 0.6, delayMs: 200 });
    window.setTimeout(() => {
      const h = mover.at(`h:${heroKey}`);
      const toX = h ? Math.max(plan.bar.x + 0.1, Math.min(plan.bar.x + plan.bar.w - 0.4, h.x + 0.35)) : plan.bar.x + 0.3;
      if (kk) faceTo(kk, toX);
      const y = plan.bar.y + 0.02;
      const from = { x: tap.x - 0.2, y }, to = { x: toX, y };
      const ms = 300 + Math.abs(to.x - from.x) * 240;
      playSfx('inn.mug_slide', { gain: 0.7 });
      const id = L.fly({ kind: 'mug', from, to, ms, arc: 0, drink: kind });
      window.setTimeout(() => {
        L.land(id);
        // the drinker's own view holds the mug (it is told to the others with their figures)
        if (live.current.mine.some((h) => h.key === heroKey)) useInn.getState().hold(heroKey, kind);
        playSfx('inn.mug', { gain: 0.6 });
        const hp = mover.at(`h:${heroKey}`);
        if (hp && hashN(cs, 0xd51) < 0.7) L.say(`h:${heroKey}`, pickBy(['Ahh.', 'Thank you kindly!', 'Just what I needed.', 'Cheers!'], hashN(cs, 0xd52)), 1800);
      }, ms);
    }, 1400);
  };

  // ---- CI5: the dice table's big moments: guests near it turn, shout, gasp ----
  const prevDice = useRef<DiceView | undefined>(data.dice);
  useEffect(() => {
    const prev = prevDice.current, next = data.dice;
    prevDice.current = next;
    if (!next || upstairs) return;
    const r = reactionOf(prev, next);
    if (!r) return;
    const tid = window.setTimeout(() => {
      const L = useLife.getState();
      const cx = plan.dice.x + plan.dice.w / 2;
      const reach = r.big ? 30 : 6;
      const near = peopleRef.current.filter((p) => { const q = mover.at(p.key); return q && Math.abs(q.x - cx) < reach && p.kind !== 'bard'; });
      const face = DICE_FACE[r.kind];
      for (const p of near) { if (!p.key.startsWith('i:reg')) faceTo(p.key, cx); if (face) L.act(p.key, face, 1800); }
      const loud = shuffleBy(near.filter((p) => !p.key.startsWith('i:reg')).map((p) => p.key).sort(), hashN(seed, next.gameN, next.throwN, next.turn), 0xd1ce).slice(0, r.big ? 3 : 2).map((key) => ({ key }));
      loud.forEach((p, i) => window.setTimeout(() => L.say(p.key, r.chorus[i % r.chorus.length]!, 1900, 'shout'), 200 + i * 300));
      // the thrower's own words over their stool
      const seat = next.seats[r.seat];
      if (seat && r.line) {
        const k = seat.kind === 'regular' ? `i:reg${r.seat}` : (() => { const h = live.current.heroes.find((x) => x.id !== undefined && x.id === seat.heroId); return h ? `h:${h.key}` : undefined; })();
        if (k) L.say(k, r.line, 2000, 'shout');
      }
      // my heroes watching from the room cheer or wince along
      const watchers = live.current.mine.filter((h) => { const q = mover.at(`h:${h.key}`); return !h.companion && q && !q.seated && Math.abs(q.x - cx) < 4; }).map((h) => h.key);
      if (watchers.length && (r.kind === 'cheer' || r.kind === 'win')) useInn.getState().flash(watchers, 'cheer', 1600);
    }, r.delayMs);
    return () => window.clearTimeout(tid);
  }, [data.dice?.throwN, data.dice?.last, data.dice?.gameN, data.dice?.state, data.dice?.turn]);

  // ---- the guest on the gallery strolls between the rooms' doors and leans on the rail ----
  // docs/121 VW1: a step a round of the World's clock, to a place along the rail from the visit's seed
  useEffect(() => {
    const gal = plan.gallery;
    if (!gal || upstairs || stage.reduced) return;
    const lo = (gal.doors[0] ?? 6) - 0.4, hi = Math.min(room.w - 4.5, (gal.doors.at(-1) ?? lo + 8) + 1.4);
    const STROLL = { period: [18, 22] as [number, number] }, PACE = 0.8;
    const xAt = (n: number) => lo + hashN(seed, n, 0x6a11) * (hi - lo);
    let last: number | undefined;
    const tick = () => {
      const cur = mover.at('i:upper');
      if (!cur || useLife.getState().acts['i:upper']) return;
      const { n, into } = wanderStep(seed, 'i:upper', serverNow() / 1000, STROLL);
      if (n === last) return;
      const first = last === undefined;
      last = n;
      const a = { x: xAt(n - 1), y: cur.y }, b = { x: xAt(n), y: cur.y };
      const along = first ? Math.min(Math.abs(b.x - a.x), into * PACE) : 0;
      const from = { x: a.x + Math.sign(b.x - a.x) * along, y: cur.y };
      if (first || Math.abs(cur.x - from.x) > 0.3) mover.place('i:upper', from, cur.face);
      const face = hashN(seed, n, 0x6a12) < 0.5 ? -1 : 1;
      if (Math.abs(b.x - from.x) < 0.05) { mover.face('i:upper', face); return; }
      mover.walk('i:upper', [from, b], { speed: PACE, end: { face } });
    };
    tick();
    const iv = window.setInterval(tick, 1000);
    return () => window.clearInterval(iv);
  }, [upstairs, seed]);

  // ---- CI8: now and then one of my heroes at the fire gives it a poke ----
  const myActs = useInn((s) => s.acts);
  const warmers = stage.mine.filter((h) => myActs[h.key] === 'warm').map((h) => h.key);
  useEffect(() => {
    if (!warmers.length || upstairs) return;
    let t = 0;
    const poke = () => {
      const ks = live.current.mine.filter((h) => useInn.getState().acts[h.key] === 'warm').map((h) => h.key);
      const k = ks[Math.floor(Math.random() * ks.length)];
      if (k) {
        useInn.getState().flash([k], 'poke', 1900);
        window.setTimeout(() => { playSfx('inn.poke', { gain: 0.7 }); useLife.getState().burst('sparks', plan.fire.x, plan.fire.y, 1500); }, 650);
      }
      t = window.setTimeout(poke, 11_000 + Math.random() * 9000);
    };
    t = window.setTimeout(poke, 3500 + Math.random() * 3000);
    return () => window.clearTimeout(t);
  }, [warmers.join(), upstairs]);

  // docs/121: another player's hero poking the fire throws the same sparks in my view
  const pokers = stage.heroes.filter((h) => h.mine === false && stage.acts?.[h.key] === 'poke').map((h) => h.key).join();
  useEffect(() => {
    if (!pokers || upstairs) return;
    const t = window.setTimeout(() => { playSfx('inn.poke', { gain: 0.45 }); useLife.getState().burst('sparks', plan.fire.x, plan.fire.y, 1500); }, 650);
    return () => window.clearTimeout(t);
  }, [pokers]);

  // ---- a guest a band sits down beside gives up the stool (the meal's seats are the view's) ----
  const moved = useRef(new Set<string>());
  useEffect(() => {
    if (upstairs) return;
    const heroes = live.current.heroes.map((h) => mover.at(`h:${h.key}`)).filter((p): p is NonNullable<typeof p> => !!p && !!p.seated);
    for (const g of crowd) {
      if (!g.at.seated || moved.current.has(g.key)) continue;
      if (!heroes.some((h) => Math.abs(h.x - g.at.x) < SEAT_GAP && Math.abs(h.y - g.at.y) < 0.5)) continue;
      moved.current.add(g.key);
      const taken = mover.keys().filter((k) => k !== g.key && !k.startsWith('w:')).map((k) => mover.at(k)!).filter(Boolean);
      const to = asideSpots(grid, taken, { x: g.at.x + 1.5, y: grid.y0 + 0.5 }, 1)[0]!;
      useLife.getState().say(g.key, pickBy(['Here, take my stool.', 'Sit, sit!', 'Plenty of room.'], hashN(seed, crowd.indexOf(g), 0x5700)), 1800);
      walk(g.key, to, 1.6);
    }
  }, [now]);

  // ---- CI16: upstairs, a night's sleep ----
  const [night2, setNight2] = useState<'off' | 'dark' | 'dawn' | 'morning'>('off');
  const sleepers = stage.mine.filter((h) => !h.companion).map((h) => h.key);
  const allAsleep = upstairs && sleepers.length > 0 && sleepers.every((k) => myActs[k] === 'sleep');
  useEffect(() => {
    if (!allAsleep) { setNight2('off'); return; }
    const ts: number[] = [];
    const at = (ms: number, fn: () => void) => ts.push(window.setTimeout(fn, ms));
    at(700, () => { playSfx('cue.candle_out', { gain: 0.8 }); setNight2('dark'); });
    at(1600, () => playSfx('amb.crickets', { gain: 0.45 }));
    for (let k = 0; k < 5; k++) at(2200 + k * 2600, () => {
      playSfx('inn.snore', { gain: 0.35 });
      const s = live.current.mine.filter((h) => !h.companion)[k % Math.max(1, sleepers.length)];
      const p = s && mover.at(`h:${s.key}`);
      if (p) useLife.getState().burst('zzz', p.x + 0.2, p.y - 0.1, 2400);
    });
    at(3200, () => setNight2('dawn'));
    at(13_200, () => playSfx('inn.rooster', { gain: 0.7 }));
    at(13_900, () => playSfx('cue.birdsong', { gain: 0.5 }));
    at(14_600, () => setNight2('morning'));
    at(16_400, () => {
      // up again: out of bed and onto the floor beside it
      const S = live.current;
      S.mine.forEach((h) => useInn.getState().act([h.key], undefined));
      const sts = poseStations(plan, POSE.asleep, S.mine.length).map((st) => ({ ...nearestFree(grid, { x: st.x + 0.4, y: st.y + 0.9 }) }));
      S.walkBand(sts, POSE.standing);
      S.mine.slice(0, 2).forEach((h, i) => window.setTimeout(() => useLife.getState().say(`h:${h.key}`, ['*yawn*', 'Morning already?', 'Slept like a log.'][i % 3]!, 2000), 600 + i * 700));
    });
    return () => { for (const t of ts) window.clearTimeout(t); };
  }, [allAsleep]);

  // ---- what is drawn ----
  const at = (x: number, y: number): CSSProperties => ({ left: Math.round(x * cellPx), top: Math.round(y * cellPx) });
  const s = scale;
  const candles = plan.candles.map((cd, i) => {
    const b = burn(i);
    const out = upstairs && night2 !== 'off';
    return { ...cd, i, h: upstairs ? 7 : Math.max(2, Math.round(9 - b.frac * 7.5)), low: !upstairs && b.frac > 0.85, out };
  });
  const sideCandle = plan.side ? { x: plan.side.x + 0.35, y: plan.side.y + 0.35 } : undefined;
  const season = look.season;
  const tableDress = upstairs ? [] : (room.tables ?? []).map((t, i) => ({ i, x: t.x + t.w / 2 + 0.25, y: t.y + 0.05 }));
  const dressArt = (i: number): PixArt | undefined => (season === 'spring' ? flowerJug(i) : season === 'summer' ? (i % 2 ? fruitBowl() : flowerJug(i)) : season === 'autumn' ? gourd() : season === 'winter' ? holly() : undefined);
  const tune = playing ? TUNES[playing.tune] : undefined;
  const beatStyle = beat ? ({ ...(tune ? { ['--tune-beat' as string]: `${(60 / tune.bpm).toFixed(3)}s` } : {}),  ['--beat' as string]: `${beat.period.toFixed(3)}s`, ['--beat-delay' as string]: `${(-(((serverNow() - (playing?.startedAtMs ?? 0)) / 1000 - beat.delay) % beat.period)).toFixed(3)}s` } as CSSProperties) : undefined;
  return (
    <div className={`fk-inn__life${beat ? ' fk-inn__life--clapping' : ''}${playing ? ' fk-inn__life--music' : ''}`} style={beatStyle ?? (tune ? ({ ['--tune-beat' as string]: `${(60 / tune.bpm).toFixed(3)}s` } as CSSProperties) : undefined)} data-testid="inn-life" data-guests={crowd.length}>
      <Furniture room={room} plan={plan} upstairs={upstairs} night={night} {...(season ? { season } : {})} feast={feast} {...(stage.sheets?.world ? { world: stage.sheets.world } : {})} seed={look.seed} scale={s} />
      {/* the candles: one on every table, burning down (server time), a fresh one put in when low */}
      {[...candles.map((c) => ({ key: `c${c.i}`, x: c.x, y: c.y, h: c.h, low: c.low, out: c.out, z: c.table >= 0 ? 205 : Math.round((room.floor + 1) * 10) + 2 })), ...(sideCandle && !upstairs ? [{ key: 'side', x: sideCandle.x, y: sideCandle.y, h: 6, low: false, out: false, z: Math.round(plan.side!.y * 10) + 2 }] : [])].map((c) => (
        <span key={c.key} className={`fk-inn__candle${c.low ? ' fk-inn__candle--low' : ''}${c.out ? ' fk-inn__candle--out' : ''}${season === 'winter' ? ' fk-inn__candle--ribbon' : ''}`} data-testid="inn-candle" data-h={c.h} style={{ ...at(c.x, c.y), zIndex: c.z, ['--h' as string]: c.h }} aria-hidden="true">
          <span className="fk-inn__candle-glow" /><span className="fk-inn__candle-stick" /><span className="fk-inn__candle-flame" />
        </span>
      ))}
      {tableDress.map((d) => { const a = dressArt(d.i); return a ? <span key={`dress${d.i}`} className="fk-inn__onTable" style={{ ...at(d.x, d.y), zIndex: 205 }} aria-hidden="true"><Pix art={a} scale={s} /></span> : null; })}
      {/* the kitchen bell, swinging when it rings */}
      {!upstairs && <span className={`fk-inn__bell${acts['i:bell'] ? ' fk-inn__bell--ring' : ''}`} style={{ ...at(plan.bell.x, plan.bell.y), zIndex: 3 }} aria-hidden="true"><Pix art={bellArt()} scale={s} /></span>}
      {/* the inn's people and its guests */}
      {people.map((p, i) => {
        const pos = mover.at(p.key) ?? p.at;
        const act = acts[p.key];
        return (
          <span key={p.key} ref={mover.reg(p.key)} className={`fk-village__figure fk-inn__guest fk-inn__guest--${p.kind}${i % 3 === 2 ? ' fk-inn__guest--nodder' : ''}`} data-testid="inn-guest" data-key={p.key} data-kind={p.kind} data-gact={act} data-face={(mover.at(p.key)?.face ?? (p.at.flip ? -1 : 1)) < 0 ? 'left' : 'right'}
            style={{ ...at(pos.x, pos.y), zIndex: Math.round(pos.y * 10) + 1 }} aria-hidden="true" onClick={clickThrough}>
            <span className="fk-village__shadow" />
            <span className="fk-village__bob" style={{ animationDelay: `${-hashN(seed, i, 31) * 20}s` }}>
              <span className="fk-village__face"><Sprite sheet={stage.sheets?.creatures} frame={p.frame + ((stage.phase + i) % 2 ? 18 : 0)} scale={s} className="fk-village__body" /></span>
            </span>
            <span className="fk-inn__bits">
              {p.kind === 'bard' && <span className="fk-inn__lute"><Pix art={luteArt()} scale={s} /></span>}
              {(p.mug || p.kind === 'regular') && (!p.seated || act === 'raise' || act === 'sip') && <span className="fk-inn__gmug"><Pix art={mugArt('ale')} scale={s} /></span>}
              {p.kind !== 'bard' && <span className="fk-inn__hands"><span /><span /></span>}
            </span>
            <span className="fk-village__plate fk-inn__gplate"><span className="fk-village__plate-name">{p.name}</span><span className="fk-village__plate-sub">{p.what}</span></span>
          </span>
        );
      })}
      {/* the serving hands, when they are out of the kitchen */}
      {(['i:cook', 'i:maid'] as const).map((k) => {
        const p = mover.at(k);
        if (!p) return null;
        return (
          <span key={k} ref={mover.reg(k)} className="fk-village__figure fk-inn__guest fk-inn__guest--hand" data-gact={acts[k]} data-face={p.face < 0 ? 'left' : 'right'} style={{ ...at(p.x, p.y), zIndex: Math.round(p.y * 10) + 1 }} aria-hidden="true">
            <span className="fk-village__shadow" />
            <span className="fk-village__face"><Sprite sheet={stage.sheets?.creatures} frame={(k === 'i:cook' ? COOK_FRAME : MAID_FRAME) + (stage.phase ? 18 : 0)} scale={s} className="fk-village__body" /></span>
            {acts[k] === 'serve' && <span className="fk-village__tray">{k === 'i:cook' ? <><Dish kind="stew" left={0} scale={s} /><Dish kind="mug" left={0} scale={s} /></> : <span className="fk-inn__newcandle" />}</span>}
          </span>
        );
      })}
      {/* seated guests' mugs on the table and the kitchen's plates (over the table top) */}
      {crowd.filter((g) => g.mug && g.at.seated && acts[g.key] !== 'raise' && acts[g.key] !== 'sip' && !moved.current.has(g.key)).map((g) => {
        const t = g.table !== undefined ? room.tables?.[g.table] : undefined;
        if (!t || dishes.some((d) => d.key === `k:${g.key}:mug`)) return null;
        return <span key={`m${g.key}`} className="fk-village__dish" style={at(g.at.x + 0.5, t.y)} aria-hidden="true"><Dish kind="mug" left={hashN(seed, g.frame) < 0.5 ? 0 : 1} scale={s} /></span>;
      })}
      {dishes.map((d) => <span key={d.key} className="fk-village__dish" data-testid="inn-dish" data-left={d.left} style={at(d.x, d.y)} aria-hidden="true"><Dish kind={d.kind} left={d.left} scale={s} /></span>)}
      <Animals stage={stage} />
      <Fx stage={stage} />
      {upstairs && <Night stage={stage} state={night2} />}
    </div>
  );
}

/** Words over heads, sparks off the fire, hearts, the clink of mugs, a mug sliding down the counter, the stick in the air. */
function Fx({ stage }: { stage: InnStage }) {
  const bubbles = useLife((s) => s.bubbles);
  const bursts = useLife((s) => s.bursts);
  const flights = useLife((s) => s.flights);
  const { mover, cellPx, scale } = stage;
  const at = (x: number, y: number): CSSProperties => ({ left: Math.round(x * cellPx), top: Math.round(y * cellPx) });
  return (
    <>
      {bubbles.map((b) => {
        const p = mover.at(b.key);
        if (!p) return null;
        return <span key={b.id} className={`fk-inn__bubble fk-inn__bubble--${b.kind}`} data-testid="inn-bubble" data-key={b.key} data-kind={b.kind} style={{ ...at(p.x + 0.5, p.y - (p.seated ? 0.05 : 0.15)), ['--life' as string]: `${b.until}ms` }} aria-hidden="true">{b.text}</span>;
      })}
      {bursts.map((b) => (
        <span key={b.id} className={`fk-inn__burst fk-inn__burst--${b.kind}`} style={{ ...at(b.x, b.y), ['--life' as string]: `${b.until}ms` }} aria-hidden="true">
          {b.kind === 'sparks' && Array.from({ length: 12 }, (_, i) => <span key={i} style={{ ['--dx' as string]: `${(hashN(b.id, i) * 2 - 1) * 26}px`, ['--dy' as string]: `${-14 - hashN(b.id, i, 2) * 30}px`, animationDelay: `${hashN(b.id, i, 3) * 0.25}s` }} />)}
          {b.kind === 'hearts' && [0, 1, 2].map((i) => <span key={i} style={{ ['--dx' as string]: `${(i - 1) * 7}px`, animationDelay: `${i * 0.28}s` }}>♥</span>)}
          {b.kind === 'zzz' && ['z', 'z', 'Z'].map((z, i) => <span key={i} style={{ animationDelay: `${i * 0.5}s` }}>{z}</span>)}
          {b.kind === 'notes' && [0, 1].map((i) => <span key={i} style={{ ['--dx' as string]: `${(hashN(b.id, i) * 2 - 1) * 12}px`, animationDelay: `${i * 0.5}s` }}>{i ? '♫' : '♪'}</span>)}
          {b.kind === 'clink' && <span>✦</span>}
        </span>
      ))}
      {flights.map((f) => (
        <span key={f.id} className={`fk-inn__flight fk-inn__flight--${f.kind}`} style={{ ...at(f.from.x, f.from.y), ['--fx' as string]: `${Math.round((f.to.x - f.from.x) * cellPx)}px`, ['--fy' as string]: `${Math.round((f.to.y - f.from.y) * cellPx)}px`, ['--arc' as string]: `${Math.round(-f.arc * cellPx)}px`, ['--ms' as string]: `${Math.round(f.ms)}ms` }} aria-hidden="true">
          <span className="fk-inn__flight-y"><Pix art={f.kind === 'mug' ? mugArt(f.drink) : stickArt()} scale={scale} /></span>
        </span>
      ))}
    </>
  );
}

/** CI16: the room upstairs at night: dark, then the window going from night to the first light. */
function Night({ stage, state }: { stage: InnStage; state: 'off' | 'dark' | 'dawn' | 'morning' }) {
  const { room, cellPx } = stage;
  const w = room.windows[0];
  return (
    <>
      <span className={`fk-inn__night fk-inn__night--${state}`} aria-hidden="true" data-testid="inn-night" data-state={state} />
      {w && <span className={`fk-inn__dawn fk-inn__dawn--${state}`} aria-hidden="true" style={{ left: Math.round(w.x * cellPx + 3 * stage.scale), top: Math.round(w.y * cellPx + 2 * stage.scale), width: Math.round((CELL - 6) * stage.scale), height: Math.round((w.h * CELL - 6) * stage.scale) }} />}
    </>
  );
}
